import { useState, useEffect, useCallback } from 'react';
import analistasService, { Analista } from '../../../../services/sementes/analistas';
import tetrazolioService from '../../../../services/sementes/tetrazolio';

interface FormValues {
  analistaId: string;
  data: Date | undefined;
  gerarBASRIR: boolean;
  solucao: string;
  repeticoes: 2 | 4;
  sementesPorRepeticao: 50 | 100;
  observacao: string;
}

interface DadosAnaliseInicial {
  data: Date;
  solucao: string;
  repeticoes: number;
  sementesPorRepeticao: number;
  observacao: string;
}

interface UseAnaliseInicialResult {
  analistas: Analista[];
  loading: boolean;
  loadingSearch: boolean;
  dadosAnaliseInicial: DadosAnaliseInicial | null;
  submitAnaliseInicial: (values: FormValues, analiseId: string) => Promise<void>;
  confirmarDadosExistentes: (values: FormValues) => void;
  cancelarAnaliseInicial: () => void;
  searchAnalistas: (query: string) => Promise<Analista[]>;
  formularioCancelado: boolean;
}

export default function useAnaliseInicial(): UseAnaliseInicialResult {
  const [analistas, setAnalistas] = useState<Analista[]>([]);
  const [analistasCache, setAnalistasCache] = useState<Analista[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [dadosAnaliseInicial, setDadosAnaliseInicial] = useState<DadosAnaliseInicial | null>(null);
  const [formularioCancelado, setFormularioCancelado] = useState(false);

  useEffect(() => {
    const loadAnalistas = async () => {
      try {
        setLoading(true);
        const { analistas: data } = await analistasService.list(1);
        setAnalistas(data);
        setAnalistasCache(data);
      } catch (error) {
        console.error('Erro ao carregar analistas:', error);
      } finally {
        setLoading(false);
      }
    };
    loadAnalistas();
  }, []);

  const searchAnalistas = useCallback(async (query: string): Promise<Analista[]> => {
    if (!query || query.trim().length === 0) {
      return analistasCache;
    }

    setLoadingSearch(true);
    try {
      const { analistas: data } = await analistasService.list(1, query);
      return data;
    } catch (error) {
      console.error('Erro ao buscar analistas:', error);
      return [];
    } finally {
      setLoadingSearch(false);
    }
  }, [analistasCache]);

  const submitAnaliseInicial = async (values: FormValues, analiseId: string) => {
    if (!values.data) {
      throw new Error('Data é obrigatória');
    }

    try {
      setLoading(true);

      const repeticoes = values.repeticoes < 4 ? 2 : 4;

      await tetrazolioService.updateAnaliseInicial(analiseId, {
        analistaId: values.analistaId,
        data: values.data,
        solucao: values.solucao,
        repeticoes,
        sementesPorRepeticao: values.sementesPorRepeticao,
        gerarBASRIR: values.gerarBASRIR,
        observacao: values.observacao,
      });

      const dados: DadosAnaliseInicial = {
        data: values.data,
        solucao: values.solucao,
        repeticoes,
        sementesPorRepeticao: values.sementesPorRepeticao,
        observacao: values.observacao,
      };

      setDadosAnaliseInicial(dados);
      setFormularioCancelado(false);
    } catch (error) {
      console.error('[useAnaliseInicial] Erro ao submeter análise inicial:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const confirmarDadosExistentes = (values: FormValues) => {
    if (!values.data) {
      console.error('[useAnaliseInicial] Data é obrigatória');
      return;
    }

    const repeticoes = values.repeticoes < 4 ? 2 : 4;

    const dados: DadosAnaliseInicial = {
      data: values.data,
      solucao: values.solucao,
      repeticoes,
      sementesPorRepeticao: values.sementesPorRepeticao,
      observacao: values.observacao,
    };

    setDadosAnaliseInicial(dados);
    setFormularioCancelado(false);
  };

  const cancelarAnaliseInicial = () => {
    setDadosAnaliseInicial(null);
    setFormularioCancelado(true);
  };

  return {
    analistas,
    loading,
    loadingSearch,
    dadosAnaliseInicial,
    submitAnaliseInicial,
    confirmarDadosExistentes,
    cancelarAnaliseInicial,
    searchAnalistas,
    formularioCancelado,
  };
}
